import SimpleCard from '../../../ui/cards/SimpleCard';
import { useTranslation } from 'react-i18next';
import '../../../styles/pages/checkoutTracking/trends.scss';
import '../../../styles/pages/checkoutTracking/uiRecommendationsElement.scss';
import statsLottie from '../../../assets/json/lottie_animation/statsInfographicsLottie.json';
import setupTrackingLottie from '../../../assets/json/lottie_animation/setup_tracking.json';
import Lottie from 'lottie-react';
import { useEffect, useState } from 'react';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import CountdownTimer from '../../../ui/timers/CountdownTimer';
import addDaysToDate from '../../../lib/helper-functions/addDaysToDate';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import NoData from '../../../ui/noData/NoData';
import { DAYS_TO_ADD } from '../../../lib/constants/abstracts';
import get_recommendations_api_call from '../../../lib/api/ai-tracker/get_recommendations_api_call';
import get_subscriptions_api_call from '../../../lib/api/ai-tracker/subscriptions/get_subscriptions_api_call';
import get_recommendation_impact_api_call from '../../../lib/api/ai-tracker/get_recommendation_impact_api_call';
import UIRecommendationsElement from './UIRecommendationsElement';
import PageBlocker from '../../../ui/cards/PageBlocker';

const UIRecommendations = () => {
  const { t } = useTranslation();
  const [activeRecommendations, setActiveRecommendations] = useState<any[]>([]);
  const [pendingRecommendations, setPendingRecommendations] = useState<any[]>(
    []
  );
  const [apiCallPendingElementID, setAPICallPendingElementID] = useState<
    string | null
  >(null);
  const [apiCallPendingElementAction, setAPICallPendingElementAction] =
    useState<string | null>(null);
  const [pausedRecommendations, setPausedRecommendations] = useState<any[]>([]);
  const [rejectedRecommendations, setRejectedRecommendations] = useState<any[]>(
    []
  );
  const [currentTab, setCurrentTab] = useState<string>('pending');
  const ai_tracker_configured_timestamp = useSelector(
    (state: RootState) => state.user.stats.ai_tracker_configured_timestamp
  );
  const tracker_access_token = useSelector(
    (state: RootState) => state.user?.stats?.ai_tracker_access_token
  );
  const ai_tracker_access_token_status = useSelector(
    (state: RootState) => state.user?.stats?.ai_tracker_access_token_status
  );
  const isTrackable = useSelector(
    (state: RootState) => state.user.stats?.is_trackable
  );
  const [isTrialActive, setIsTrialActive] = useState<boolean>(false);
  const [trialRemainingDays, setTrialRemainingDays] = useState<number>(0);
  const [isPaymentActive, setIsPaymentActive] = useState<boolean>(false);
  const [isTrialEnded, setIsTrialEnded] = useState<boolean>(false);

  const futureDate = addDaysToDate(
    ai_tracker_configured_timestamp ? ai_tracker_configured_timestamp : '',
    DAYS_TO_ADD
  );

  const [isTimesUp, setIsTimesUp] = useState<boolean>(false);
  const [loading, setLoading] = useState<
    'pending' | 'success' | 'error' | 'null'
  >('null');

  useEffect(() => {
    if (isTimesUp && ai_tracker_access_token_status === 'pending') {
      setLoading('pending');
    } else if (isTimesUp && ai_tracker_access_token_status === 'error') {
      setLoading('error');
    } else if (isTimesUp && ai_tracker_access_token_status === 'null') {
      setLoading('null');
    }
    if (isTimesUp && tracker_access_token !== '') {
      getSubscriptions();
    }
  }, [
    isTimesUp,
    tracker_access_token,
    isTrackable,
    ai_tracker_access_token_status,
  ]);

  const calculateRemainingDays = (endDate: string): number => {
    const now: Date = new Date();
    const end: Date = new Date(endDate);

    const startOfToday: Date = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0
    );

    const differenceInTime: number = end.getTime() - startOfToday.getTime();

    const differenceInDays: number = Math.floor(
      differenceInTime / (1000 * 60 * 60 * 24)
    );

    return differenceInDays >= 0 ? differenceInDays : 0;
  };

  type actionPropTypes =
    | '/element/enable'
    | '/element/disable'
    | '/element/pause'
    | '/element/resume'
    | '/element/reject'
    | '/element/unarchive'
    | '/select'
    | '/unselect';

  const recommendationActionsAPICalls = async (
    id: string,
    method: actionPropTypes
  ) => {
    try {
      const res = await get_recommendations_api_call('PATCH', method, id);
      return res;
    } catch (error) {
      console.error('API call failed:', error);
      return false;
    }
  };

  const getImpactOfRecommendation = async (
    rec_id: string,
    tracker_access_token: string
  ) => {
    try {
      const res = await get_recommendation_impact_api_call(
        rec_id,
        tracker_access_token
      );
      if (res) {
        return res;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error fetching recommendation impact:', error);
      return null;
    }
  };

  useEffect(() => {
    const fetchAndSetImpacts = async () => {
      if (!tracker_access_token || activeRecommendations?.length === 0) return;

      const needsUpdate = activeRecommendations?.some(
        (item) =>
          item?.impact === null || item?.control_group_conversion_rate === null
      );
      if (!needsUpdate) return; // stop loop if already updated

      const updated = await Promise.all(
        activeRecommendations?.map(async (item) => {
          if (
            item.impact !== null &&
            item.control_group_conversion_rate !== null
          ) {
            return item; // already has impact
          }
          const res = await getImpactOfRecommendation(
            item.element_id,
            tracker_access_token
          );
          return {
            ...item,
            impact: res?.impact ?? null,
            control_group_conversion_rate:
              res?.control_group_conversion_rate ?? null,
          };
        })
      );

      setActiveRecommendations(updated);
    };

    fetchAndSetImpacts();
  }, [activeRecommendations, tracker_access_token]);

  const getSubscriptions = async () => {
    try {
      setLoading('pending');
      const res = await get_subscriptions_api_call();

      if (res) {
        if (res?.length > 0) {
          const currentSubscription = res[res?.length - 1];
          const is_trial_active = currentSubscription?.is_trial_active;
          const is_active = currentSubscription?.is_active;

          if (is_trial_active === 0 && is_active === 0) {
            // trial expired and paid package inactive
            setIsTrialActive(false);
            setIsPaymentActive(false);
            setIsTrialEnded(true);
          }
          if (is_trial_active === 1 && is_active === 0) {
            // trial expired
            setIsPaymentActive(false);
            setIsTrialActive(false);
            setIsTrialEnded(true);
          }
          if (is_trial_active === 1 && is_active === 1) {
            // trial active and package not yet bought
            setIsTrialActive(true);
            setIsPaymentActive(false);
            const trialEndDate = currentSubscription?.end_date;
            const remainingDays = calculateRemainingDays(trialEndDate);
            setTrialRemainingDays(remainingDays);
          }
          if (is_trial_active === 0 && is_active === 1) {
            // trial expired and paid package active
            setIsPaymentActive(true);
            setIsTrialActive(false);
          }
        } else {
          //trial not started and package not activated
          //    setIsTrialPopupOpen(true);
          setIsTrialActive(false);
          setIsPaymentActive(false);
        }
        setLoading('success');
        getRecommendations();
      } else {
        setLoading('null');
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setLoading('error');
    }
  };

  /* ---------------- HELPER FUNCTION TO STRUCTURE RECOMMENDATIONS START-----------------*/
  const extractRecommendationsFromResponse = (data: any) => {
    // Check if the data is an array or an object
    if (!Array.isArray(data)) {
      const objectValues = Object.values(data);
      let allElementsArray: any = [];
      // Check if the object values are not empty
      if (objectValues?.length > 0) {
        // loop over the object values and push them to the array
        objectValues?.forEach((item: any) => {
          if (item?.length > 0) {
            item?.forEach((element: any) => {
              allElementsArray?.push(element);
            });
          }
        });
      }

      return allElementsArray;
    } else {
      return [];
    }
  };
  /* ---------------- HELPER FUNCTION TO STRUCTURE RECOMMENDATIONS END-----------------*/

  const getRecommendations = async () => {
    setLoading('pending');
    try {
      const res = await get_recommendations_api_call('POST', '/get');
      //   const res = dummy_res.data;
      if (res) {
        setLoading('success');
        const { pending, active, paused, rejected } = res;

        // this is to add empty values to the recommendations to show the impact and control group conversion rate in the UI
        const withPlaceholders = (items: any[]) =>
          items.map((item) => ({
            ...item,
            impact: null,
            control_group_conversion_rate: null,
          }));

        // Handling pending recommendations
        setPendingRecommendations(
          withPlaceholders(extractRecommendationsFromResponse(pending))
        );
        // Handling active recommendations
        setActiveRecommendations(
          withPlaceholders(extractRecommendationsFromResponse(active))
        );
        // Handling paused recommendations
        setPausedRecommendations(
          withPlaceholders(extractRecommendationsFromResponse(paused))
        );
        // Handling rejected recommendations
        setRejectedRecommendations(
          withPlaceholders(extractRecommendationsFromResponse(rejected))
        );
      } else {
        setLoading('null');
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      setLoading('error');
    }
  };

  // console.log(pendingRecommendations);
  // console.log(activeRecommendations);
  // console.log(pausedRecommendations);
  // console.log(rejectedRecommendations);

  /* ----Handlers for recommendations actions----------- */
  const handleToggleStyle = async (
    tab: string,
    elementId: string,
    styleId: string,
    newStatus: boolean
  ) => {
    const action = newStatus ? '/select' : '/unselect';
    setAPICallPendingElementID(elementId);
    setAPICallPendingElementAction(action);

    try {
      const success = await recommendationActionsAPICalls(styleId, action);

      if (success) {
        const stateUpdater = {
          pending: setPendingRecommendations,
          active: setActiveRecommendations,
          paused: setPausedRecommendations,
          rejected: setRejectedRecommendations,
        }[tab];

        stateUpdater?.((prev) =>
          prev.map((element) => {
            if (element.element_id === elementId) {
              return {
                ...element,
                styles: element?.styles?.map((style: any) =>
                  style?.id === styleId
                    ? { ...style, is_active: newStatus }
                    : style
                ),
              };
            }
            return element;
          })
        );
      }
    } finally {
      setAPICallPendingElementID(null);
      setAPICallPendingElementAction(null);
    }
  };

  const moveRecommendation = (from: string, to: string, element: any) => {
    const stateSetters = {
      pending: setPendingRecommendations,
      active: setActiveRecommendations,
      paused: setPausedRecommendations,
      rejected: setRejectedRecommendations,
    };

    stateSetters[from as keyof typeof stateSetters]?.((prev) =>
      prev.filter((item) => item.element_id !== element.element_id)
    );

    stateSetters[to as keyof typeof stateSetters]?.((prev) => [
      ...prev,
      element,
    ]);
  };

  const handleRecommendationAction = async (
    element: any,
    currentStatus: string,
    action: actionPropTypes,
    targetStatus: string
  ) => {
    setAPICallPendingElementID(element.element_id);
    setAPICallPendingElementAction(action);

    try {
      const success = await recommendationActionsAPICalls(
        element.element_id,
        action
      );

      if (success) {
        moveRecommendation(currentStatus, targetStatus, element);
      }
    } finally {
      setAPICallPendingElementID(null);
      setAPICallPendingElementAction(null);
    }
  };

  return (
    <>
      {!ai_tracker_configured_timestamp && <PageBlocker />}
      {loading === 'pending' && <LoadingSpinner />}
      {loading === 'error' && (
        <NoData
          title={t('ErrorFetchingData')}
          subTitle={t('SomethingWentWrong-msg')}
        />
      )}

      <div className='flex-row-full-width-flex-wrap'>
        {isTrackable && !isTimesUp && (
          <SimpleCard>
            <div className='trends'>
              <h3>{t('TrendsText1')}</h3>
              <div className='trends-lottie'>
                <Lottie animationData={statsLottie} loop={true} />
              </div>
            </div>
          </SimpleCard>
        )}
        {!isTrackable && !isTimesUp && (
          <SimpleCard>
            <div className='trends'>
              <h3>{t('TrendsText2')}</h3>
              <div className='trends-lottie'>
                <Lottie animationData={setupTrackingLottie} loop={true} />
              </div>
            </div>
          </SimpleCard>
        )}

        {/* Recommendation Tabs section start here */}
        {isTimesUp && loading === 'success' && (
          <div className='normalForm'>
            <div className='normalForm__top'>
              <div className='recommendations--all'>
                <div
                  style={{
                    marginTop:
                      isPaymentActive && !isTrialActive ? '0rem' : '3rem',
                  }}
                  className='recommendationsTabs'
                  data-recommendations-tab={currentTab}
                >
                  {/* ============================== */}
                  <div
                    onClick={() => {
                      setCurrentTab('pending');
                    }}
                    className='recommendationsTabs__tab recommendationsTabs__tab--pending'
                  >
                    <h2>{t('New')}</h2>
                    <span>{`(${pendingRecommendations?.length})`}</span>
                  </div>
                  {/* ============================== */}
                  <div
                    onClick={() => {
                      setCurrentTab('active');
                    }}
                    className='recommendationsTabs__tab recommendationsTabs__tab--active'
                  >
                    <h2>{t('ActiveUpper')}</h2>
                    <span>{`(${activeRecommendations?.length})`}</span>
                  </div>
                  {/* ============================== */}
                  <div
                    onClick={() => {
                      setCurrentTab('paused');
                    }}
                    className='recommendationsTabs__tab recommendationsTabs__tab--paused'
                  >
                    <h2>{t('Paused')}</h2>
                    <span>{`(${pausedRecommendations?.length})`}</span>
                  </div>
                  {/* ============================== */}
                  <div
                    onClick={() => {
                      setCurrentTab('rejected');
                    }}
                    className='recommendationsTabs__tab recommendationsTabs__tab--rejected'
                  >
                    <h2>{t('Rejected')}</h2>
                    <span>{`(${rejectedRecommendations?.length})`}</span>
                  </div>
                  {/* ============================== */}
                </div>

                {/* suggested recommendations */}
                {currentTab === 'pending' && loading === 'success' && (
                  <div className='recommendations'>
                    <h2>{t('Recommendations')}</h2>
                    <h3>{t('SuggestedRecommendationsText')}</h3>

                    {pendingRecommendations?.length === 0 && (
                      <div className='uiRecommendationsElement__container--none'>
                        <NoData title={t('NoRecommendations')} />
                      </div>
                    )}
                    {pendingRecommendations?.length > 0 && (
                      <div className='uiRecommendationsElement__container'>
                        {/* {!isPaymentActive && !isTrialActive && (
                          <div className='uiRecommendationsElement__container--overlay'></div>
                        )} */}
                        {/* Here we render elements */}
                        {pendingRecommendations?.map((item: any) => (
                          <UIRecommendationsElement
                            key={item?.element_id}
                            elementData={item}
                            recommendationCurrentStatus={'pending'}
                            apiCallPendingElementID={apiCallPendingElementID}
                            apiCallPendingElementAction={
                              apiCallPendingElementAction
                            }
                            onToggleStyle={(_, elementId, styleId, newStatus) =>
                              handleToggleStyle(
                                'pending',
                                elementId,
                                styleId,
                                newStatus
                              )
                            }
                            onAction={async (
                              element,
                              currentStatus,
                              action,
                              targetStatus
                            ) => {
                              await handleRecommendationAction(
                                element,
                                currentStatus,
                                action,
                                targetStatus
                              );
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* active recommendations */}
                {currentTab === 'active' && (
                  <div className='recommendations'>
                    <h2> {t('ActiveRecommendations')}</h2>
                    <h3>{t('ActiveRecommendationsText')}</h3>
                    {activeRecommendations?.length === 0 && (
                      <div className='uiRecommendationsElement__container--none'>
                        <NoData title={t('NoActiveRecommendations')} />
                      </div>
                    )}
                    {activeRecommendations?.length > 0 && (
                      <div className='uiRecommendationsElement__container'>
                        {/* {!isPaymentActive && !isTrialActive && (
                          <div className='uiRecommendationsElement__container--overlay'></div>
                        )} */}
                        {/* Here we render elements */}
                        {activeRecommendations?.map((item: any) => (
                          <UIRecommendationsElement
                            key={item?.element_id}
                            elementData={item}
                            recommendationCurrentStatus={'active'}
                            apiCallPendingElementID={apiCallPendingElementID}
                            apiCallPendingElementAction={
                              apiCallPendingElementAction
                            }
                            onToggleStyle={(_, elementId, styleId, newStatus) =>
                              handleToggleStyle(
                                'active',
                                elementId,
                                styleId,
                                newStatus
                              )
                            }
                            onAction={async (
                              element,
                              currentStatus,
                              action,
                              targetStatus
                            ) => {
                              await handleRecommendationAction(
                                element,
                                currentStatus,
                                action,
                                targetStatus
                              );
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* paused recommendations */}
                {currentTab === 'paused' && (
                  <div className='recommendations'>
                    <h2> {t('PausedRecommendations')}</h2>
                    <h3>{t('PausedRecommendationsText')}</h3>
                    {pausedRecommendations?.length === 0 && (
                      <div className='uiRecommendationsElement__container--none'>
                        <NoData title={t('NoPausedRecommendations')} />
                      </div>
                    )}
                    {pausedRecommendations?.length > 0 && (
                      <div className='uiRecommendationsElement__container'>
                        {/* {!isPaymentActive && !isTrialActive && (
                          <div className='uiRecommendationsElement__container--overlay'></div>
                        )} */}
                        {/* Here we render elements */}
                        {pausedRecommendations?.map((item: any) => (
                          <UIRecommendationsElement
                            key={item?.element_id}
                            elementData={item}
                            recommendationCurrentStatus={'paused'}
                            apiCallPendingElementID={apiCallPendingElementID}
                            apiCallPendingElementAction={
                              apiCallPendingElementAction
                            }
                            onToggleStyle={(_, elementId, styleId, newStatus) =>
                              handleToggleStyle(
                                'paused',
                                elementId,
                                styleId,
                                newStatus
                              )
                            }
                            onAction={async (
                              element,
                              currentStatus,
                              action,
                              targetStatus
                            ) => {
                              await handleRecommendationAction(
                                element,
                                currentStatus,
                                action,
                                targetStatus
                              );
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* rejected recommendations */}
                {currentTab === 'rejected' && (
                  <div className='recommendations'>
                    <h2> {t('RejectedRecommendations')}</h2>
                    <h3>{t('RejectedRecommendationsText')}</h3>
                    {rejectedRecommendations?.length === 0 && (
                      <div className='uiRecommendationsElement__container--none'>
                        <NoData title={t('NoRejectedRecommendations')} />
                      </div>
                    )}
                    {rejectedRecommendations?.length > 0 && (
                      <div className='uiRecommendationsElement__container'>
                        {/* {!isPaymentActive && !isTrialActive && (
                          <div className='uiRecommendationsElement__container--overlay'></div>
                        )} */}
                        {/* Here we render elements */}
                        {rejectedRecommendations?.map((item: any) => (
                          <UIRecommendationsElement
                            key={item?.element_id}
                            elementData={item}
                            recommendationCurrentStatus={'rejected'}
                            apiCallPendingElementID={apiCallPendingElementID}
                            apiCallPendingElementAction={
                              apiCallPendingElementAction
                            }
                            onToggleStyle={(_, elementId, styleId, newStatus) =>
                              handleToggleStyle(
                                'rejected',
                                elementId,
                                styleId,
                                newStatus
                              )
                            }
                            onAction={async (
                              element,
                              currentStatus,
                              action,
                              targetStatus
                            ) => {
                              await handleRecommendationAction(
                                element,
                                currentStatus,
                                action,
                                targetStatus
                              );
                            }}
                          />
                        ))}
                      </div>
                    )}
                    {/* ------------------------------- */}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <CountdownTimer
          prefix={t('StillCollectingData')}
          suffix={`${t('remaining')}.`}
          className='dashboardTab__item--left-subtitle'
          futureDate={futureDate}
          setIsTimesUp={setIsTimesUp}
          display={false}
        />
      </div>
    </>
  );
};

export default UIRecommendations;
